import type { DevelopmentStatus } from '@/shared/utils/format-status.js';

export type DashboardKpisResponse = {
    totalDevelopments: number;
    elaboracaoFuncional: number;
    alocacaoFabrica: number;
    aprovacaoEstimativa: number;
    emDesenvolvimento: number;
    emCorrecao: number;
    validacaoFuncional: number;
    encerrados: number;
    cancelados: number;
};

export type DashboardKpisProps = DashboardKpisResponse;

export class DashboardKpisModel {
    private constructor(private readonly props: DashboardKpisProps) {}

    public static with(props: DashboardKpisProps): DashboardKpisModel {
        return new DashboardKpisModel(props);
    }

    public static withFromResponse(response: DashboardKpisResponse): DashboardKpisModel {
        return DashboardKpisModel.with({ ...response });
    }

    public get total(): number {
        return this.props.totalDevelopments;
    }

    public get elaboracaoFuncional(): number {
        return this.props.elaboracaoFuncional;
    }

    public get alocacaoFabrica(): number {
        return this.props.alocacaoFabrica;
    }

    public get aprovacaoEstimativa(): number {
        return this.props.aprovacaoEstimativa;
    }

    public get emDesenvolvimento(): number {
        return this.props.emDesenvolvimento;
    }

    public get emCorrecao(): number {
        return this.props.emCorrecao;
    }

    public get validacaoFuncional(): number {
        return this.props.validacaoFuncional;
    }

    public get encerrados(): number {
        return this.props.encerrados;
    }

    public percentageOf(value: number): string {
        if (this.props.totalDevelopments <= 0) {
            return '0%';
        }
        return `${((value / this.props.totalDevelopments) * 100).toFixed(1)}%`;
    }

    public toObject(): DashboardKpisProps {
        return { ...this.props };
    }
}

export type StatusCountResponse = {
    status: DevelopmentStatus;
    count: number;
};

export type StatusCountProps = {
    status: DevelopmentStatus;
    count: number;
};

export class StatusCountModel {
    private constructor(private readonly props: StatusCountProps) {}

    public static with(props: StatusCountProps): StatusCountModel {
        return new StatusCountModel(props);
    }

    public static withFromResponse(response: StatusCountResponse): StatusCountModel {
        return StatusCountModel.with({ ...response });
    }

    public static withFromResponseList(list: StatusCountResponse[]): StatusCountModel[] {
        return list.map(StatusCountModel.withFromResponse);
    }

    public get status(): DevelopmentStatus {
        return this.props.status;
    }

    public get count(): number {
        return this.props.count;
    }

    public toObject(): StatusCountProps {
        return { ...this.props };
    }
}

export type RecentDevResponse = {
    id: string;
    displayId: string;
    title: string;
    status: DevelopmentStatus;
    projectName: string;
    clientName: string;
    updatedAt: string;
};

export type RecentDevProps = RecentDevResponse;

export class RecentDevModel {
    private constructor(private readonly props: RecentDevProps) {}

    public static with(props: RecentDevProps): RecentDevModel {
        return new RecentDevModel(props);
    }

    public static withFromResponse(response: RecentDevResponse): RecentDevModel {
        return RecentDevModel.with({ ...response });
    }

    public static withFromResponseList(list: RecentDevResponse[]): RecentDevModel[] {
        return list.map(RecentDevModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get displayId(): string {
        return this.props.displayId;
    }

    public get title(): string {
        return this.props.title;
    }

    public get status(): DevelopmentStatus {
        return this.props.status;
    }

    public get updatedAt(): string {
        return this.props.updatedAt;
    }

    public get projectName(): string {
        return this.props.projectName;
    }

    public get clientName(): string {
        return this.props.clientName;
    }

    public toObject(): RecentDevProps {
        return { ...this.props };
    }
}

export type SapAlertResponse = {
    clientId: string;
    clientName: string;
    reason: 'mcp_unreachable' | 'mcp_auth_failed' | 'system_ambiguous';
};

export type DashboardDataResponse = {
    totals: DashboardKpisResponse;
    byStatus: StatusCountResponse[];
    recentDevelopments: RecentDevResponse[];
    sapAlerts?: SapAlertResponse[];
};

export type DashboardDataProps = {
    kpis: DashboardKpisModel;
    statusDistribution: StatusCountModel[];
    recentDevelopments: RecentDevModel[];
    sapAlerts: SapAlertResponse[];
};

export class DashboardDataModel {
    private constructor(private readonly props: DashboardDataProps) {}

    public static with(props: DashboardDataProps): DashboardDataModel {
        return new DashboardDataModel(props);
    }

    public static withFromResponse(response: DashboardDataResponse): DashboardDataModel {
        return DashboardDataModel.with({
            kpis: DashboardKpisModel.withFromResponse(response.totals),
            statusDistribution: StatusCountModel.withFromResponseList(response.byStatus ?? []),
            recentDevelopments: RecentDevModel.withFromResponseList(response.recentDevelopments ?? []),
            sapAlerts: response.sapAlerts ?? []
        });
    }

    public get kpis(): DashboardKpisModel {
        return this.props.kpis;
    }

    public get statusDistribution(): StatusCountModel[] {
        return this.props.statusDistribution;
    }

    public get recentDevelopments(): RecentDevModel[] {
        return this.props.recentDevelopments;
    }

    public get sapAlerts(): SapAlertResponse[] {
        return this.props.sapAlerts;
    }

    public toObject(): DashboardDataProps {
        return { ...this.props };
    }
}
